import { useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, X, Plus } from "lucide-react";
import { fullMessage } from "@/lib/sourceLimit";

/**
 * Compact editor for extra source URLs the editor wants to add manually
 * before generating an article. Matches the "Write custom article" UX.
 *
 * Controlled: parent owns `urls` (string[]) and receives changes via
 * `onChange(nextUrls)`. Keeps the widget stateless w.r.t. persistence.
 *
 * `maxUrls` (optional): how many URLs this box may hold before the
 * writer's source limit is reached (see lib/sourceLimit.js). The parent
 * computes it from the sources already counted elsewhere. Omit it for
 * no limit.
 */
export default function ExtraSourcesEditor({
  urls,
  onChange,
  disabled = false,
  testIdPrefix = "extra-source",
  label = "Add more sources",
  hint = "Paste a URL and hit Add. We'll crawl each one alongside the LLM-found sources.",
  maxUrls,
}) {
  const [input, setInput] = useState("");
  const atLimit = typeof maxUrls === "number" && urls.length >= maxUrls;

  const add = (raw) => {
    const u = (raw || "").trim();
    if (!u) return;
    if (atLimit) {
      toast.error(fullMessage());
      return;
    }
    try {
      const parsed = new URL(u);
      const clean = parsed.toString().replace(/#$/, "");
      if (urls.includes(clean)) {
        toast.error("Already added");
        return;
      }
      onChange([...urls, clean]);
      setInput("");
    } catch {
      toast.error("Enter a valid URL (starting with http:// or https://)");
    }
  };

  const remove = (u) => onChange(urls.filter((x) => x !== u));

  return (
    <div className="space-y-2">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
          {label}
        </label>
        <p className="text-[11px] text-[var(--ink-muted)] mb-1.5">{hint}</p>
        <div className="flex gap-2">
          <input
            type="url"
            data-testid={`${testIdPrefix}-input`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(input);
              }
            }}
            disabled={disabled || atLimit}
            placeholder={atLimit ? "Source limit reached" : "https://example.com/story"}
            className="flex-1 min-w-0 px-2 py-1.5 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] disabled:opacity-60"
          />
          <button
            type="button"
            data-testid={`${testIdPrefix}-add-btn`}
            onClick={() => add(input)}
            disabled={disabled || atLimit || !input.trim()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>
      {urls.length > 0 && (
        <ul className="space-y-1">
          {urls.map((u, i) => (
            <li
              key={u}
              data-testid={`${testIdPrefix}-row-${i}`}
              className="flex items-start gap-2 text-xs bg-[var(--bg)] border border-[var(--border)] px-2 py-1.5"
            >
              <LinkIcon className="w-3 h-3 mt-0.5 text-[var(--ink-muted)] shrink-0" />
              <a
                href={u}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 break-all text-[var(--accent)] hover:underline"
              >
                {u}
              </a>
              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-mono-display shrink-0">
                manual
              </span>
              <button
                type="button"
                data-testid={`${testIdPrefix}-remove-${i}`}
                onClick={() => remove(u)}
                disabled={disabled}
                className="p-0.5 text-[var(--ink-muted)] hover:text-red-600 disabled:opacity-50"
                title="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
